import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Modal, Fade, Typography, Divider } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';

import api from '../../utils/functions/api';
import { confirmAsync } from '../../utils/functions/globalDialog';
import * as actions from '../../redux/actions';
import { ExpensesTableApplove } from '../../components/Table';
import AddpayoutForm from '../../components/Forms/AddpayoutForm';
import ExpensesFilterPanel from '../../components/Card/ExpensesFilterPanel';
import { buildExpensesQuery } from '../../utils/functions/buildExpensesQuery';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import { useExpenseSortPref } from '../../hooks/useExpenseSortPref';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: '80%', md: '60%' },
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  maxHeight: '90vh',
  overflow: 'auto',
};

const PAGE_SIZE = 20;

// ชุด chip กรองสถานะของแท็บนี้ — เฉพาะ status ที่ backlog เจอได้จริงตาม getTabMatch ฝั่ง backend
// (ACTIVE_STATUSES ตัด SUCCESS ออกแล้ว) ไม่มี Success chip เพราะกดแล้วจะไม่เจอรายการเลย
const STATUS_OPTIONS = [
  { key: 'ALL', label: 'ทั้งหมด', color: 'default', statuses: [] },
  { key: 'PENDING', label: 'Pending', color: 'warning', statuses: ['PENDING', 'PREAPPROVE', 'APPROVE_1', 'APPROVE_2'] },
  { key: 'APPROVE', label: 'Approved', color: 'warning', statuses: ['APPROVE'] },
  { key: 'PREPARE', label: 'Prepared', color: 'info', statuses: ['PREPARE'] },
];

// tab "ค้างดำเนินการ" (backlog) — กล่อง "ตัวกรอง" ใช้ ExpensesFilterPanel (shared component แก้ layout
// ทีเดียวมีผลทุก tab) ตัว tab นี้เก็บแค่ state + fetchPage/handler ที่เป็นของ tab นี้เอง (handler ชุด
// อนุมัติ/ปฏิเสธ/จ่ายเงิน ไม่เหมือนกันทุก tab เลยยังแยกเก็บที่นี่)
export default function ExpensesTabBacklog({ onDoubleClickProject, refreshTick = 0 }) {
  const tabKey = 'backlog';
  const showDateRange = true;

  const dispatch = useDispatch();
  const me = useSelector((state) => state.me);
  const info = useSelector((state) => state.info);
  const projects = useSelector((state) => state.project);
  const budgetState = useSelector((state) => state.budget);

  const [dateBounds, setDateBounds] = useState({ min: null, max: null });
  const [filterOpen, setFilterOpen] = useState(true);
  const [maxLookbackText, setMaxLookbackText] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [expensesData, setExpensesData] = useState({ rows: [], total: 0, isLoading: false });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [projectSelect, setProjectSelect] = useState(null);
  const [budgetSelect, setBudgetSelect] = useState(null);
  const [filterVat, setFilterVat] = useState(false);
  const [filterWithholding, setFilterWithholding] = useState(false);
  // เลือกได้หลาย chip พร้อมกัน (multi-select) — array ว่าง = "ทั้งหมด" ไม่กรอง
  const [statusFilters, setStatusFilters] = useState([]);
  const toggleStatusFilter = (key) => {
    if (key === 'ALL') { setStatusFilters([]); return; }
    setStatusFilters((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };
  const [dateFilterField, setDateFilterField] = useState('date');
  const [appliedDateFilterField, setAppliedDateFilterField] = useState('date');
  const [dateRange, setDateRange] = useState(null);
  // ใช้ร่วมกับแท็บ "ค้าง" ของ Profile (expenseQueue) — ดู useExpenseSortPref.js
  const { sortBy, sortDir, setSort } = useExpenseSortPref(tabKey);

  const [advSearchName, setAdvSearchName] = useState('');
  const [advSearchCode, setAdvSearchCode] = useState('');
  const [advSearchPayee, setAdvSearchPayee] = useState('');
  const [advSearchApplicant, setAdvSearchApplicant] = useState('');
  const [advSearchNetAmount, setAdvSearchNetAmount] = useState('');

  const [openPay, setOpenPay] = useState(false);
  const [exSelect, setExSelect] = useState('');
  const [datePay, setDatePay] = useState(new Date());
  const [paidType] = useState('TRANSFER');
  const [slipImage, setSlipImage] = useState([]);

  const { control, handleSubmit, reset, setValue } = useForm();
  const dateRangeRef = useRef(null);

  useEffect(() => {
    dispatch(actions.projectAll({ lite: true }));
  }, [dispatch]);

  // ช่วงวันที่จริงของรายการเบิกที่อยู่ใน tab นี้เท่านั้น (ไม่ใช่ทั้งระบบ) — ใช้ bound date-range picker
  useEffect(() => {
    if (!showDateRange) return;
    (async () => {
      try {
        const { data } = await api.get(`${process.env.REACT_APP_API_URL}/expenses/date-range?tab=${tabKey}&field=${dateFilterField}`);
        setDateBounds({
          min: data?.min ? new Date(data.min) : null,
          max: data?.max ? new Date(data.max) : null,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  }, [dateFilterField]);

  useEffect(() => {
    if (projectSelect?._id) {
      dispatch(actions.budgetAll({ project_id: projectSelect._id }));
    }
  }, [dispatch, projectSelect]);

  // infinite scroll — โหลดทีละหน้า (PAGE_SIZE รายการ) แล้วต่อท้าย rows เดิม แทนการกดเปลี่ยนหน้า
  const fetchPage = useCallback(async (targetPage, append, overrides = {}) => {
    const searchVal = overrides.search ?? search;
    const dateRangeVal = overrides.dateRange ?? dateRange;
    setExpensesData((prev) => ({ ...prev, isLoading: true }));
    try {
      const q = buildExpensesQuery({
        tab: tabKey,
        page: targetPage,
        size: PAGE_SIZE,
        sortBy,
        sortDir,
        search: searchVal,
        projectId: projectSelect?._id,
        budgetId: budgetSelect?._id,
        filterVat,
        filterWithholding,
        statusFilters,
        statusOptions: STATUS_OPTIONS,
        dateRange: dateRangeVal,
        dateFilterField,
        advSearch: {
          name: advSearchName,
          code: advSearchCode,
          payee: advSearchPayee,
          applicant: advSearchApplicant,
          netAmount: advSearchNetAmount,
        },
      });
      const { data } = await api.get(`${process.env.REACT_APP_API_URL}/expenses?${q.toString()}`);
      const newRows = data?.rows || [];
      setExpensesData((prev) => ({
        rows: append ? [...prev.rows, ...newRows] : newRows,
        total: data?.total || 0,
        isLoading: false,
      }));
      setPage(targetPage);
    } catch (error) {
      console.error(error);
      setExpensesData((prev) => ({ ...prev, isLoading: false }));
    }
  }, [
    search, projectSelect, budgetSelect, filterVat, filterWithholding, statusFilters,
    dateRange, dateFilterField, advSearchName, advSearchCode, advSearchPayee, advSearchApplicant,
    advSearchNetAmount, sortBy, sortDir,
  ]);

  const fetchData = useCallback(() => fetchPage(1, false), [fetchPage]);

  // ใช้ ref เก็บ fetchPage ตัวล่าสุดไว้เรียกจาก effect โดยไม่ให้ effect ต้อง re-run ทุกครั้งที่ filter เปลี่ยน
  // เพราะฟิลเตอร์พวกนี้ต้องรอกดปุ่ม "ค้นหา" เท่านั้นถึงจะ apply — ยกเว้น refreshTick/statusFilters/sortBy/sortDir
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  useEffect(() => {
    fetchPageRef.current(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick, statusFilters, sortBy, sortDir]);

  const handleSearch = () => {
    const nextSearch = searchInput;
    const nextDateRange = showDateRange
      ? (dateRangeRef.current?.getValue ? dateRangeRef.current.getValue() : dateRange)
      : dateRange;
    setSearch(nextSearch);
    setDateRange(nextDateRange);
    setAppliedDateFilterField(dateFilterField);
    fetchPage(1, false, { search: nextSearch, dateRange: nextDateRange });
  };

  const handleLoadMore = () => {
    if (expensesData.isLoading) return;
    fetchPage(page + 1, true);
  };

  const hasMore = expensesData.rows.length < expensesData.total;

  const mutate = async (id, payload) => {
    await dispatch(actions.expensesPut(id, payload));
    fetchData();
  };

  const actorPayload = () => ({
    approval_actor: me?.userData?.id,
    approval_actor_name: formatEmployeeThaiName(me?.userData),
  });

  const handlePreApprove = (id) => mutate(id, { status: 'PREAPPROVE', ...actorPayload() });
  const handleApprove1 = (id) => mutate(id, { status: 'APPROVE_1', ...actorPayload() });
  const handleApprove2 = (id) => mutate(id, { status: 'APPROVE_2', ...actorPayload() });
  const handleApprove3 = (id) => mutate(id, { status: 'APPROVE', ...actorPayload() });
  // อนุมัติแบบ 1 step — เก็บชื่อเป็น "ผู้มีสิทธิ์อนุมัติคนที่ 1" (info.expense_approver_1) เสมอ
  // แทนชื่อคนที่กดจริง (approval_actor id ยังเป็นคนที่กดจริงเหมือนเดิม เปลี่ยนแค่ชื่อที่โชว์)
  const handleApprove = (id) => mutate(id, {
    status: 'APPROVE',
    approval_actor: me?.userData?.id,
    approval_actor_name: formatEmployeeThaiName(info?.expense_approver_1) || formatEmployeeThaiName(me?.userData),
  });
  const handleSuccess = (id) => mutate(id, { status: 'SUCCESS' });
  const handleReject1 = (id) => mutate(id, { status: 'REJECT', ...actorPayload() });
  const handleReject2 = (id) => mutate(id, { status: 'REJECT_2', ...actorPayload() });
  const handleReject3 = (id) => mutate(id, { status: 'REJECT_3', ...actorPayload() });
  const handleReject = (id) => mutate(id, { status: 'REJECT', ...actorPayload() });
  const handleHold = (id) => mutate(id, { status: 'HOLD' });
  const handleBillPickup = (id) => mutate(id, { bill_pickup: true });
  const handleReset = (id) => mutate(id, { status: 'PENDING', ...actorPayload() });
  const handlePrepareStatus = (id) => mutate(id, { status: 'PREPARE' });

  const handleOpenPay = (e) => {
    setOpenPay(true);
    setExSelect(e);
    const net =
      e?.net_price != null
        ? Number(e.net_price)
        : (Number(e?.price) || 0) - (Number(e?.withholding_tax) || 0);
    setValue('price', net > 0 ? net : '');
  };
  const handleClosePay = () => setOpenPay(false);

  const onSubmitPay = async (data) => {
    const confirm = await confirmAsync('บันทึกข้อมูล');
    if (!confirm) return;
    const dataSubmit = {
      ...data,
      paidType,
      date: datePay,
      employee: me?.userData?._id,
      expenses: exSelect?.id,
      budget: exSelect?.budget?._id,
      slipImage,
    };
    await dispatch(actions.payoutCreate(dataSubmit));
    await dispatch(actions.payoutAll({}));
    await dispatch(actions.expensesPut(exSelect?._id, { status: 'SUCCESS' }));
    fetchData();
    reset();
    setSlipImage([]);
    handleClosePay();
  };

  const handleClearAdvanced = () => {
    setAdvSearchName('');
    setAdvSearchCode('');
    setAdvSearchPayee('');
    setAdvSearchApplicant('');
    setAdvSearchNetAmount('');
    setProjectSelect(null);
    setBudgetSelect(null);
    setFilterVat(false);
    setFilterWithholding(false);
  };

  return (
    <Box>
      <ExpensesFilterPanel
        filterOpen={filterOpen}
        onToggleFilterOpen={() => setFilterOpen((v) => !v)}
        maxLookbackText={maxLookbackText}
        showDateRange={showDateRange}
        dateRangeRef={dateRangeRef}
        dateRange={dateRange}
        dateBounds={dateBounds}
        dateFilterField={dateFilterField}
        onDateFieldChange={setDateFilterField}
        onMaxLookbackChange={setMaxLookbackText}
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onSearch={handleSearch}
        onClearSearch={() => { setSearchInput(''); setSearch(''); fetchPage(1, false, { search: '' }); }}
        advancedOpen={advancedOpen}
        onToggleAdvanced={() => setAdvancedOpen((v) => !v)}
        projects={projects}
        projectSelect={projectSelect}
        onProjectChange={(val) => { setProjectSelect(val); setBudgetSelect(null); }}
        budgets={budgetState}
        budgetSelect={budgetSelect}
        onBudgetChange={setBudgetSelect}
        filterVat={filterVat}
        onFilterVatChange={setFilterVat}
        filterWithholding={filterWithholding}
        onFilterWithholdingChange={setFilterWithholding}
        advSearchName={advSearchName}
        onAdvSearchNameChange={setAdvSearchName}
        advSearchCode={advSearchCode}
        onAdvSearchCodeChange={setAdvSearchCode}
        advSearchPayee={advSearchPayee}
        onAdvSearchPayeeChange={setAdvSearchPayee}
        advSearchApplicant={advSearchApplicant}
        onAdvSearchApplicantChange={setAdvSearchApplicant}
        advSearchNetAmount={advSearchNetAmount}
        onAdvSearchNetAmountChange={setAdvSearchNetAmount}
        onClearAdvanced={handleClearAdvanced}
        appliedDateFilterField={appliedDateFilterField}
        appliedDateRange={dateRange}
      />

      <ExpensesTableApplove
        expenses={expensesData}
        paginationMode="infinite"
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        highlightQuery={search}
        filter={expensesData.rows}
        statusFilters={statusFilters}
        onStatusFilterToggle={toggleStatusFilter}
        statusOptions={STATUS_OPTIONS}
        handlePreApprove={handlePreApprove}
        handleApprove={handleApprove}
        handleApprove1={handleApprove1}
        handleApprove2={handleApprove2}
        handleApprove3={handleApprove3}
        handleReject1={handleReject1}
        handleReject2={handleReject2}
        handleReject3={handleReject3}
        handleReject={handleReject}
        handleSuccess={handleSuccess}
        handleBillPickup={handleBillPickup}
        handleHold={handleHold}
        handleReset={handleReset}
        handlePrepareStatus={handlePrepareStatus}
        handleOpenPay={handleOpenPay}
        onDetailClose={fetchData}
        onDoubleClickProject={onDoubleClickProject}
        me={me}
        info={info}
        tabVariant="backlog"
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={setSort}
      />

      <Modal open={openPay} onClose={handleClosePay} closeAfterTransition>
        <Fade in={openPay}>
          <Box sx={style}>
            <Typography variant="h6" component="h2" className="mb-4">
              เพิ่มข้อมูลการชำระเงิน
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <form onSubmit={handleSubmit(onSubmitPay)}>
              <AddpayoutForm
                control={control}
                Controller={Controller}
                date={datePay}
                setDate={setDatePay}
                paidType={paidType}
                expenseData={exSelect}
                slipImage={slipImage}
                setSlipImage={setSlipImage}
              />
            </form>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
}
